import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Chip, Divider, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { RestorationInput } from 'models/order-input/restoration-input';
import { TeethSelectionType } from 'models/order';
import { formatTeethList, formatTeethRange } from 'utils/teeth';
import { propertiesFromInputs, simpleCategoryForType } from 'components/order-flow/shared/restoration-input-helper';

type ConstructionBoxProps = {
    restoration: RestorationInput;
    onDeleteClick: () => void;
    onModifyClick?: () => void;
    disableDelete?: boolean;
};
const ConstructionBox: FC<ConstructionBoxProps> = ({ restoration, onDeleteClick, onModifyClick, disableDelete }) => {
    const { t: translate } = useTranslation(['construction-props', 'common', 'orders']);
    const category = simpleCategoryForType(restoration.type);
    const properties = propertiesFromInputs(restoration.properties);

    const subTitle = () => {
        const { teeth } = restoration;
        switch (teeth.type) {
            case TeethSelectionType.TEETH:
                return formatTeethList(teeth.selectedTeeth ?? []);
            case TeethSelectionType.RANGE:
                return teeth.selectedRange ? formatTeethRange(teeth.selectedRange.start, teeth.selectedRange.end) : '';
            case TeethSelectionType.JAW:
                return teeth.selectedJaw ? translate(`orders:${teeth.selectedJaw}`) : '';
            default:
                return '';
        }
    };

    const camelToUpperSnake = (str: string): string => {
        return str.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
    };

    return (
        <Card sx={{ width: '100%' }} variant="outlined">
            <Box p={2} display="flex">
                <Box
                    display="flex"
                    margin="auto"
                    mr={2}
                    sx={{ backgroundColor: '#eee', borderRadius: 5, padding: 1.5 }}
                >
                    <img style={{ maxHeight: 25 }} src={`/images/restoration-icons/${category}.png`} loading="lazy" />
                </Box>
                <Box flexGrow={1} alignContent="center">
                    <Box display="flex" alignItems="center">
                        <Box flexGrow={1} display="flex">
                            <Typography variant="h5">
                                {translate(`orders:${category}`)} {subTitle()}
                            </Typography>
                            {properties.material ? (
                                <Fragment>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ marginLeft: 0.75, marginRight: 0.75 }}
                                    />
                                    <Typography variant="h5">{String(properties.material)}</Typography>
                                </Fragment>
                            ) : undefined}
                            {restoration.warranty && (
                                <Chip sx={{ marginLeft: 1 }} label={translate(`orders:WARRANTY`)} size="small" />
                            )}
                            {restoration.isTioTandvard && (
                                <Chip sx={{ marginLeft: 1 }} label={translate(`orders:TIOTANDVARD`)} size="small" />
                            )}
                        </Box>
                        {!disableDelete ? (
                            <Box display="flex" alignItems="center">
                                {onModifyClick && (
                                    <Button
                                        color="primary"
                                        size="small"
                                        onClick={onModifyClick}
                                        data-test="construction-box.modify-btn"
                                    >
                                        {translate('orders:MODIFY_CONSTRUCTION')}
                                    </Button>
                                )}
                                <IconButton size="small" onClick={onDeleteClick}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ) : undefined}
                    </Box>
                    {restoration.properties.length ? (
                        <Box>
                            <Box display="flex" flexWrap="wrap">
                                {properties.shade ? (
                                    <Box mr={2}>
                                        <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                            {translate(`construction-props:SHADE`)}:
                                        </Typography>{' '}
                                        <Typography variant="caption">{translate(`${properties.shade}`)}</Typography>
                                    </Box>
                                ) : undefined}
                                {properties.color ? (
                                    <Box mr={2}>
                                        <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                            {translate(`construction-props:COLOR`)}:
                                        </Typography>{' '}
                                        <Typography variant="caption">{translate(`${properties.color}`)}</Typography>
                                    </Box>
                                ) : undefined}
                                {properties.colorSampling ? (
                                    <Box mr={2}>
                                        <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                            {translate(`construction-props:COLOR_SAMPLING_AT_LAB`)}:
                                        </Typography>{' '}
                                        <Typography variant="caption">{translate('common:YES')}</Typography>
                                    </Box>
                                ) : undefined}
                                {Object.entries(properties).map(([key, value]) => (
                                    <Fragment key={key}>
                                        {value &&
                                        key !== 'material' &&
                                        key !== 'color' &&
                                        key !== 'shade' &&
                                        key !== 'colorSampling' &&
                                        key !== 'warrantyType' ? (
                                            <Box mr={2}>
                                                <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                                    {translate(`construction-props:${camelToUpperSnake(key)}`)}:
                                                </Typography>{' '}
                                                <Typography variant="caption">{translate(`${value}`)}</Typography>
                                            </Box>
                                        ) : undefined}
                                    </Fragment>
                                ))}
                            </Box>
                        </Box>
                    ) : undefined}
                </Box>
            </Box>
        </Card>
    );
};

export default ConstructionBox;
